import React from 'react';
import cn from 'classnames';
import {
  BadgeCell,
  BadgeCellComponentProps,
  ChipsCell,
  ChipsCellComponentProps,
  ChipsSkeleton,
  Column,
  DropdownButtonCell,
  DropdownButtonCellComponentProps,
  getFaIcon,
  MenuItemProps,
  getFixedWidthFaIcon,
  DropdownButtonSkeleton,
} from '@odin-labs/components';
import {
  CardStatus,
  WorkerCardType,
  WorkerCardStatus,
} from 'apollo/generated/client-operations';
import { WorkerCard } from 'graphql/server/queries/types';
import { ensureNonEmptyItems } from 'utils';
import {
  getISODateTimeToFormattedDate,
  getPrettyFormattedDateTime,
} from 'utils/dates';
import { to } from 'acl';
import { AuthUser } from 'auth/types';
import {
  faIdBadge,
  faMobile,
  faQrcode,
  faIdCard,
  faMinusOctagon,
  faRedo,
} from 'components/icons';
import { WorkerAccessHistory } from 'containers/worker/types';

const cardStatusConfig: Record<
  CardStatus,
  Pick<BadgeCellComponentProps, 'text' | 'color'>
> = {
  Active: { text: 'Active', color: 'green' },
  Inactive: { text: 'Inactive', color: 'gray' },
  SentToWorker: { text: 'Sent to worker', color: 'yellow' },
  Failed: { text: 'Failed', color: 'red' },
};

const workerCardStatusConfig: Record<
  WorkerCardStatus,
  Pick<BadgeCellComponentProps, 'text' | 'color'>
> = {
  created: { text: 'Created', color: 'gray' },
  printing: { text: 'Printing...', color: 'blue' },
  printed: { text: 'Printed', color: 'purple' },
  printFailed: { text: 'Print Failed', color: 'red' },
  deactivated: { text: 'Deactivated', color: 'gray' },
};

export const getWorkerCardStatusConfig = (
  workerCard: WorkerCard,
): Pick<BadgeCellComponentProps, 'text' | 'color'> => {
  const { workerCardFormat, cardStatus, workerCardStatus } = workerCard ?? {};
  return workerCardFormat?.cardType === WorkerCardType.Pvc &&
    workerCardStatus !== WorkerCardStatus.Printed
    ? workerCardStatusConfig[workerCardStatus]
    : cardStatusConfig[cardStatus];
};

const IdCardIcon = getFixedWidthFaIcon({ icon: faIdCard });
const MinusOctagonIcon = getFixedWidthFaIcon({ icon: faMinusOctagon });
const RedoIcon = getFixedWidthFaIcon({ icon: faRedo });

export const getWorkerCardTableColumns = ({
  openReactivateBadgeModal,
  openDeactivateBadgeModal,
  openReprintBadgeModal,
  submitIssueMobileBadge,
  user,
}: {
  openReactivateBadgeModal: (workerCard: WorkerCard) => void;
  openDeactivateBadgeModal: (workerCard: WorkerCard) => void;
  openReprintBadgeModal: (workerCard: WorkerCard) => void;
  submitIssueMobileBadge: (
    workerCard: WorkerCard,
    target?: 'worker' | 'apple' | 'google',
  ) => void;
  user: AuthUser;
}): Column<WorkerCard>[] => {
  const isNotContractorMember = !user.isContractor;
  const canDeactivateBadges = user.isAllowed(to.deactivateBadges);
  const canGenerateWalletWorkerCard = user.isAllowed(
    to.generateWalletWorkerCard,
  );

  return ensureNonEmptyItems<Column<WorkerCard>>([
    {
      Header: 'Badge Number',
      id: 'badgeNumber',
      accessor: 'cardNumber',
    },
    {
      Header: 'Status',
      id: 'status',
      Cell: BadgeCell,
      componentProps: (workerCard: WorkerCard): BadgeCellComponentProps => {
        const statusConfig = getWorkerCardStatusConfig(workerCard);
        return {
          text: statusConfig?.text,
          color: statusConfig?.color,
          className: cn('odin-uppercase odin-leading-relaxed odin-font-bold'),
        };
      },
    },
    {
      Header: 'Authorization',
      id: 'authorization',
      accessor: (workerCard: WorkerCard): string => {
        return workerCard.isTemporary ? 'Temporary' : 'Permanent';
      },
      cellClassName: cn('odin-text-center'),
      headerClassName: cn('odin-w-full odin-text-center'),
    },
    {
      Header: 'Badge Type',
      id: 'badgeType',
      Cell: ChipsCell,
      CellLoading: ChipsSkeleton,
      componentProps: (workerCard: WorkerCard): ChipsCellComponentProps => {
        const { cardType } = workerCard?.workerCardFormat ?? {};
        let cardIcon = faIdBadge;
        if (cardType === WorkerCardType.MobileBadge) {
          cardIcon = faMobile;
        } else if (cardType === WorkerCardType.QrCode) {
          cardIcon = faQrcode;
        }
        return {
          chips: [
            {
              text: workerCard.workerCardFormat?.name,
              secondaryText:
                workerCard.workerCardFormat?.facilityCode?.toString(),
              withDot: false,
              icon: getFaIcon({ icon: cardIcon }),
            },
          ],
        };
      },
    },
    {
      Header: 'Issued On',
      id: 'issuedOn',
      accessor: (workerCard: WorkerCard): string =>
        getPrettyFormattedDateTime(workerCard.issuedAt) ?? 'None',
    },
    {
      Header: 'Expires On',
      id: 'expiresOn',
      accessor: (workerCard: WorkerCard): string =>
        getPrettyFormattedDateTime(workerCard.expiredAt) ?? 'None',
    },
    isNotContractorMember &&
      user.isAllowed(to.seeBadgeActions) && {
        Header: '',
        id: 'actions',
        Cell: DropdownButtonCell,
        CellLoading: (): React.ReactElement => (
          <DropdownButtonSkeleton cellAlignment="right" />
        ),
        componentProps: (
          workerCard: WorkerCard,
        ): DropdownButtonCellComponentProps => {
          const { cardType } = workerCard.workerCardFormat ?? {};
          const adminBadgeActions = ensureNonEmptyItems<MenuItemProps>([
            cardType === WorkerCardType.MobileBadge &&
              canGenerateWalletWorkerCard && {
                icon: IdCardIcon,
                text: 'Generate Apple Badge',
                theme: 'secondary',
                onClick: (): void => {
                  submitIssueMobileBadge(workerCard, 'apple');
                },
              },
            cardType === WorkerCardType.MobileBadge &&
              canGenerateWalletWorkerCard && {
                icon: IdCardIcon,
                text: 'Generate Google Badge',
                theme: 'secondary',
                onClick: (): void => {
                  submitIssueMobileBadge(workerCard, 'google');
                },
              },
          ]);
          const activeBadgeActions = ensureNonEmptyItems<MenuItemProps>([
            canDeactivateBadges && {
              icon: MinusOctagonIcon,
              text: 'Deactivate Badge',
              theme: 'danger',
              onClick: (): void => {
                openDeactivateBadgeModal(workerCard);
              },
            },
            cardType === WorkerCardType.MobileBadge && {
              icon: RedoIcon,
              text: 'Re-issue Mobile Badge',
              theme: 'secondary',
              onClick: (): void => {
                submitIssueMobileBadge(workerCard, 'worker');
              },
            },
            cardType === WorkerCardType.Pvc && {
              icon: RedoIcon,
              text: 'Reprint',
              theme: 'secondary',
              onClick: (): void => {
                openReprintBadgeModal(workerCard);
              },
            },
          ]);
          const inactiveBadgeActions: MenuItemProps[] = [
            {
              icon: RedoIcon,
              text: 'Reactivate Badge',
              theme: 'secondary',
              onClick: (): void => {
                openReactivateBadgeModal(workerCard);
              },
            },
          ];

          return {
            cellAlignment: 'right',
            menuItems: [
              ...(workerCard.cardStatus === CardStatus.Inactive
                ? inactiveBadgeActions
                : activeBadgeActions),
              ...adminBadgeActions,
            ],
          };
        },
      },
  ]);
};

export const getJobsiteAccessHistoryColumns =
  (): Column<WorkerAccessHistory>[] => {
    return [
      {
        id: 'changeDate',
        Header: 'Date of action',
        accessor: (accessHistory: WorkerAccessHistory): string =>
          getPrettyFormattedDateTime(accessHistory?.changeDate),
      },
      {
        id: 'changeToAccess',
        Header: 'Change to access',
        accessor: (accessHistory: WorkerAccessHistory): string => {
          if (accessHistory?.changeToAccess !== 'Allowed') {
            return accessHistory?.banEndDate ? 'Suspended' : 'Banned';
          }
          return 'Allowed';
        },
      },
      {
        id: 'jobsiteName',
        Header: 'Site',
        accessor: (accessHistory: WorkerAccessHistory): string =>
          accessHistory?.jobsiteName,
      },
      {
        id: 'changeCategory',
        Header: 'Category',
        accessor: (accessHistory: WorkerAccessHistory): string =>
          accessHistory?.changeCategory,
      },
      {
        id: 'changeReason',
        Header: 'Reason',
        accessor: (accessHistory: WorkerAccessHistory): string =>
          accessHistory?.changeReason,
      },
      {
        id: 'banEndDate',
        Header: 'Suspension end date',
        accessor: (accessHistory: WorkerAccessHistory): string =>
          getISODateTimeToFormattedDate(accessHistory?.banEndDate),
      },
      {
        id: 'changeApprovedBy',
        Header: 'Change approved by',
        accessor: (accessHistory: WorkerAccessHistory): string =>
          accessHistory?.changeApprovedBy,
      },
      {
        id: 'updatedBy',
        Header: 'Change updated by',
        accessor: (accessHistory: WorkerAccessHistory): string =>
          accessHistory?.updatedByName,
      },
    ];
  };
